import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { formatLakhs } from "../../lib/utils";
import {
  normalizePortfolioScheduleListResponse,
  PortfolioScheduleListItem,
} from "../../lib/portfolio-stats";
import { ConsolidatedCashflowTab } from "../../components/portfolio/ConsolidatedCashflowTab";
import { ConsolidatedCashflowKpiStrip } from "../../components/portfolio/ConsolidatedCashflowKpiStrip";
import apiService from "../../services/apiService";

const ManagePortfolio = () => {
  const [rows, setRows] = useState<PortfolioScheduleListItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const loadPortfolioSchedules = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const response = await apiService.get<unknown>(
        "/rental-schedule/get-rental-schedule-cashflow-list"
      );
      setRows(normalizePortfolioScheduleListResponse(response));
    } catch (error) {
      console.error("Failed to load portfolio rental schedules:", error);
      setRows([]);
      setListError("Could not load rental schedules.");
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPortfolioSchedules();
  }, [loadPortfolioSchedules]);

  const portfolioDetailPath = (schedule: PortfolioScheduleListItem) =>
    `/manage-portfolio/${encodeURIComponent(String(schedule.rsId))}`;

  return (
    <div className="min-h-screen bg-white w-full">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur">
        <div className="space-y-5 w-full px-4 py-2 bg-white">
            <h1 className="text-2xl font-semibold tracking-tight">
              Manage Portfolio
            </h1>
        </div>
      </header>

      <main className="space-y-5 w-full px-4 py-2 bg-white">
        <Tabs defaultValue="cashflow" className="space-y-5">
          <TabsList>
            <TabsTrigger value="cashflow">Consolidated Cashflow</TabsTrigger>
            <TabsTrigger value="overview">Overview</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-5 mt-0">
            <section>
              <ConsolidatedCashflowKpiStrip />
            </section>


            <Card className="overflow-hidden border-border/60 shadow-sm">
              <div className="flex items-center justify-between px-5 py-3.5 border-b bg-gradient-to-r from-muted/50 to-muted/20">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-1 rounded-full bg-primary" />
                  <div>
                    <h2 className="text-sm font-semibold">Active Rental Schedules</h2>
                    <p className="text-[11px] text-muted-foreground">Portfolio overview across all customers and lenders</p>
                  </div>
                </div>
                <Badge variant="secondary" className="font-medium">{rows.length} schedules</Badge>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-muted/40 backdrop-blur">
                    <TableRow className="hover:bg-transparent border-b-2">
                      <TableHead className="w-12 text-xs uppercase tracking-wide font-semibold text-muted-foreground/80">#</TableHead>
                      <TableHead className="text-xs uppercase tracking-wide font-semibold text-muted-foreground/80">Customer</TableHead>
                      <TableHead className="text-xs uppercase tracking-wide font-semibold text-muted-foreground/80">RS No</TableHead>
                      <TableHead className="text-xs uppercase tracking-wide font-semibold text-muted-foreground/80">Assigned Lender</TableHead>
                      <TableHead className="text-right text-xs uppercase tracking-wide font-semibold text-muted-foreground/80">Monthly Rental</TableHead>
                      <TableHead className="text-center text-xs uppercase tracking-wide font-semibold text-muted-foreground/80">Tenure</TableHead>
                      <TableHead className="text-xs uppercase tracking-wide font-semibold text-muted-foreground/80">Start Date</TableHead>
                      <TableHead className="text-right text-xs uppercase tracking-wide font-semibold text-muted-foreground/80">Upfront Revenue</TableHead>
                      <TableHead className="text-right text-xs uppercase tracking-wide font-semibold text-muted-foreground/80">Cashflow (This Month)</TableHead>
                      <TableHead className="text-right text-xs uppercase tracking-wide font-semibold text-muted-foreground/80">Cashflow to Date</TableHead>
                      <TableHead className="text-center text-xs uppercase tracking-wide font-semibold text-muted-foreground/80">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {listLoading ? (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center text-muted-foreground py-10">
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading rental schedules…
                          </span>
                        </TableCell>
                      </TableRow>
                    ) : listError ? (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center py-10">
                          <p className="text-destructive mb-3">{listError}</p>
                          <Button variant="outline" size="sm" onClick={() => void loadPortfolioSchedules()}>
                            Retry
                          </Button>
                        </TableCell>
                      </TableRow>
                    ) : rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center text-muted-foreground py-10">
                          No active rental schedules yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row, idx) => (
                        <TableRow key={row.rsId} className="hover:bg-primary/5 transition-colors odd:bg-muted/10 group">
                          <TableCell className="text-muted-foreground tabular-nums text-xs">{idx + 1}</TableCell>
                          <TableCell className="font-medium">{row.customerName}</TableCell>
                          <TableCell>
                            <Link
                              to={portfolioDetailPath(row)}
                              className="text-primary hover:underline font-medium tabular-nums"
                            >
                              RS-{row.rsNo}
                            </Link>
                          </TableCell>
                          <TableCell>
                            {row.lenderName
                              ? <span className="text-sm">{row.lenderName}</span>
                              : <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">Unassigned</Badge>}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{formatLakhs(row.monthlyRental)}</TableCell>
                          <TableCell className="text-center tabular-nums text-muted-foreground">{row.tenureMonths} mo</TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {row.rentalStartDate
                              ? format(new Date(row.rentalStartDate), "dd MMM yyyy")
                              : "—"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{formatLakhs(row.upfrontRevenue)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatLakhs(row.cashflowThisMonth)}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold text-foreground">{formatLakhs(row.cashflowToDate)}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant={row.status === "Active" ? "default" : "secondary"} className="text-[10px]">{row.status}</Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>

          </TabsContent>

          <TabsContent value="cashflow" className="mt-0 w-full">
            <ConsolidatedCashflowTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ManagePortfolio;
